/**
 * Redux slice for the epi-chat feature.
 *
 * State lives here (rather than local useState) so that the conversation
 * survives route changes and so the preview slide-over — coming in a follow-up
 * PR — can read the same message/tool-call stream without re-subscribing to
 * the socket.
 */

import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

import type { WireScope } from '../api/scopeWire';
import type {
  AvSelectionCheckpoint,
  EpiArtifact,
  EpiArtifactExecution,
  EpiArtifactResult,
  EpiArtifactTab,
  EpiAttachment,
  EpiAttritionStep,
  EpiChatSendPayload,
  EpiCheckpoint,
  EpiClarifyingQuestion,
  EpiConceptGroupCode,
  EpiConceptGroupDefinition,
  EpiConceptGroupUsage,
  EpiExecutionConceptUsage,
  EpiExecutionCoverage,
  EpiExecutionDistribution,
  EpiExecutionTechnicalChecks,
  EpiInternalLogEntry,
  EpiMessage,
  EpiMessagePart,
  EpiResourceRef,
  EpiRunState,
  EpiSurface,
  EpiToolCall,
  EpiUploadedFile,
} from '../types/epi-chat-types';

/**
 * Map a tool name to the artifact-drawer tab whose surface it produces.
 * When the agent invokes one of these tools the drawer's active tab snaps
 * to the matching surface so the epi watches the result come in live
 * instead of having to chase it.
 *
 * Unknown tools don't switch tabs — the drawer holds whatever the user is
 * currently reading. This is also exported so the stream reducer can apply
 * it without re-importing the table.
 */
export const TOOL_TAB_MAP: Readonly<Record<string, EpiArtifactTab>> = {
  draft_sql: 'sql',
  edit_sql: 'sql',
  verify_analytical_variable: 'verification',
  execute_analytical_variable: 'verification',
  compute_analytical_variable_distribution: 'verification',
  dry_run_sql: 'verification',
  lint_sql: 'verification',
  compute_results: 'results',
  sample_rows: 'results',
  preview_concept_group: 'codes',
  resolve_concepts: 'codes',
};

export interface EpiChatState {
  sessionId: string | null;
  runState: EpiRunState;
  runId: string | null;
  messages: EpiMessage[];
  queue: EpiChatSendPayload[];
  artifacts: EpiArtifact[];
  activeArtifactId: string | null;
  /**
   * Active tab on the right-side artifact drawer. Set explicitly by the
   * user clicking a tab, or implicitly by the stream reducer when a tool
   * call lands that maps to a tab (see {@link TOOL_TAB_MAP}). `null`
   * means "use the default for this artifact's kind" — the drawer
   * resolves that to `description` for analytical variables and `description` (or
   * `definition` if rule-based) for concept groups.
   */
  activeArtifactTab: EpiArtifactTab | null;
  error: string | null;
  /**
   * Set when the backend reports the run's pod died mid-flight
   * (``STATUS_UPDATE { kind: 'run_interrupted' }`` — lease expired on a
   * cross-pod reconnect; see ``docs/DESIGN_MULTIPOD_RUN_LIFECYCLE.md`` D4).
   * Drives the in-transcript Retry card so the user can escape a frozen
   * "Agent is working…" by re-firing their last turn. ``null`` means no
   * interrupted run is outstanding.
   *
   * Cleared the instant a new run begins — by ``runStarted`` (covers both
   * the post-retry ``RUN_STARTED`` and a ``kind:'run' status:'streaming'``
   * re-announce for a run that actually executes elsewhere) and by
   * ``userMessageSent`` (optimistic clear the moment Retry/compose fires).
   * Kept separate from ``error`` so the card is the only affordance —
   * setting ``error`` too would double up with the red Alert banner.
   */
  interruptedRun: { runId: string; retryable: boolean } | null;
  /**
   * True until the backend confirms the session is ready for input.
   *
   * Defaults to ``true`` on a fresh session so the composer stays disabled
   * during the gap between "socket connected" and "welcome stream started" —
   * otherwise the input briefly enables, the user can type a stray character,
   * then it disables again when the welcome STATUS_UPDATE arrives. Cleared by
   * the welcome-complete STATUS_UPDATE, by ``hydrated`` (resumed sessions skip
   * the welcome and come back enabled), or on any terminal path (cancel,
   * error).
   */
  initializing: boolean;
  /**
   * True between ``send('subscribe', …)`` and the backend's ``ready`` ack
   * (or the ``session_replay`` snapshot, whichever lands first). Drives the
   * "loading session…" affordance in the chat pane so opening an existing
   * session shows progress instead of an empty pane while the round-trip
   * + S3 synth is in flight. Cleared by ``subscribeReady`` and ``hydrated``.
   * Distinct from ``initializing`` because that flag specifically tracks the
   * welcome-stream lifecycle on brand-new sessions; the two can be true
   * simultaneously in the brand-new-session case and the loading copy
   * differs.
   */
  subscribing: boolean;
  /**
   * Submit gate: ``true`` only once the backend's ``ready`` ack for the
   * current subscribe has been applied. Defaults to ``false`` (= block) so the
   * composer cannot submit during the load handshake — including the frame
   * after the socket connects but before ``subscribeStarted``'s effect runs,
   * and the frame where ``hydrated`` has reset ``runState`` to ``idle`` but the
   * in-flight run is not yet known. Unlike ``subscribing`` this is deliberately
   * NOT cleared by ``hydrated``: a localStorage/replay snapshot tells us what to
   * render, not whether a request is still in flight — only ``ready`` (which
   * carries ``inflightRun``) does. Set ``true`` by ``subscribeReady`` and, as a
   * dropped-ack backstop, by any run-lifecycle reducer. Reset ``false`` by
   * ``subscribeStarted``. OR-6139 AC 2.
   */
  subscribeAcked: boolean;
  /**
   * Bumped each time the backend refuses a subscribe with a *retryable*
   * code (``SUBSCRIBE_UNAVAILABLE`` — a transient/infra read failure, not a
   * genuine authz denial). The page hook watches this nonce to schedule a
   * bounded-backoff re-subscribe before surfacing the terminal panel.
   */
  subscribeRetryNonce: number;
  /**
   * Monotonically-increasing seq of the most recent AG-UI event we've
   * processed for this session. Used as a resume cursor: on reconnect the
   * subscribe handshake passes ``since_seq`` and the backend replays any
   * buffered events newer than this from its per-session ring buffer.
   * Persisted so a hard refresh mid-stream doesn't lose the cursor.
   */
  lastEventSeq: number;
  /**
   * The latest BE-broadcast focus scope for this session (OR-5963 step 2).
   *
   * Set by ``STATUS_UPDATE { kind: 'focus_scope' }`` — the agent declaring
   * "I'm switching focus to <artifact>". Consumed by the project page's
   * ``useFocusScopeNavigator`` hook, which flips the URL when this changes
   * to something different from the URL-derived scope.
   *
   * Latest-wins, defensive: malformed payloads (failed Zod parse) are
   * dropped silently; the previous value stays. Reset to ``null`` on
   * session change so cross-session bleed-through can't navigate the
   * user somewhere unexpected.
   */
  // TODO: cleanup with research_assistant_gen_3 ff
  remoteFocusScope: WireScope | null;
  /**
   * Patient-data dataset the BE orchestrator is currently bound to,
   * surfaced for the legacy ``/epi-study-agent`` pin-control. Wire
   * shape is the fully-qualified ``EXPORT.<schema>`` string the BE
   * already normalises into. ``null`` means "no dataset pinned" — the
   * agent will refuse SQL tools until the user picks one. Echoed by
   * the BE on subscribe + after every ``set_active_dataset`` so a
   * multi-tab session stays in sync without polling. Project-bound
   * chats never receive this echo (the dataset is derived from the
   * project) and the slot stays ``null``; the pin-control is hidden
   * in that surface.
   */
  activeDatasetName: string | null;
}

// OR-6348: sentinel run id for the optimistic "RA is working" state fired by
// ``sendMessage`` on every plain text send, so the indicator appears before
// the real ``RUN_STARTED`` round-trips back. Exported so the stream reducer's
// ERROR handler can recognize it and reset ``runState`` if the backend never
// actually starts a run (e.g. ``NOT_SUBSCRIBED``) — the socket send that
// triggers it has no ack to ``.catch()``, unlike the upload/resume REST calls.
export const OPTIMISTIC_SEND_RUN_ID = 'optimistic-send';

export const initialEpiChatState: EpiChatState = {
  sessionId: null,
  runState: 'idle',
  runId: null,
  messages: [],
  queue: [],
  artifacts: [],
  activeArtifactId: null,
  activeArtifactTab: null,
  error: null,
  interruptedRun: null,
  initializing: true,
  subscribing: false,
  subscribeAcked: false,
  subscribeRetryNonce: 0,
  lastEventSeq: 0,
  remoteFocusScope: null,
  activeDatasetName: null,
};

const ensureAssistantMessage = (state: EpiChatState, runId: string): EpiMessage => {
  const last = state.messages[state.messages.length - 1];
  if (last && last.role === 'assistant' && last.status === 'streaming') {
    return last;
  }
  const message: EpiMessage = {
    id: nanoid(),
    role: 'assistant',
    parts: [],
    status: 'streaming',
    timestamp: new Date().toISOString(),
  };
  state.messages.push(message);
  void runId;
  return message;
};

const appendTextPart = (message: EpiMessage, text: string) => {
  const last = message.parts[message.parts.length - 1];
  if (last && last.kind === 'text') {
    last.text += text;
    return;
  }
  message.parts.push({ kind: 'text', text });
};

const findToolCallRecursive = (
  calls: EpiToolCall[] | undefined,
  toolId: string
): EpiToolCall | undefined => {
  if (!calls) return undefined;
  for (const call of calls) {
    if (call.toolId === toolId) return call;
    const sub = findToolCallRecursive(call.subCalls, toolId);
    if (sub) return sub;
  }
  return undefined;
};

const findToolPartAnywhere = (state: EpiChatState, toolId: string): EpiToolCall | undefined => {
  for (const message of state.messages) {
    for (const part of message.parts) {
      if (part.kind === 'tool_call') {
        if (part.toolCall.toolId === toolId) return part.toolCall;
        const nested = findToolCallRecursive(part.toolCall.subCalls, toolId);
        if (nested) return nested;
      }
    }
  }
  return undefined;
};

/**
 * Tools that answer themselves via a separate UI surface (e.g. INPUT_REQUEST
 * renders a ClarifyingQuestionCard). Suppress the generic tool-call card so
 * the user doesn't see a redundant spinner alongside the thing they're
 * actually being asked to interact with.
 */
const SUPPRESSED_TOOL_CARDS = new Set([
  'ask_clarifying_question',
  'checkpoint',
  'propose_analytical_variable_checkpoint',
  'request_approval',
]);

const closeRunningRecursive = (calls: EpiToolCall[] | undefined) => {
  if (!calls) return;
  for (const call of calls) {
    if (call.status === 'running') call.status = 'complete';
    closeRunningRecursive(call.subCalls);
  }
};

/**
 * Close every running tool call except the one currently being started.
 * Strands' adapter doesn't emit TOOL_CALL_END, and the agent is single-threaded,
 * so when a new tool begins or the model emits text we can safely infer that
 * any preceding running tool has finished.
 */
const closeOtherRunningTools = (state: EpiChatState, exceptToolId?: string) => {
  for (const message of state.messages) {
    for (const part of message.parts) {
      if (part.kind !== 'tool_call') continue;
      if (part.toolCall.toolId !== exceptToolId && part.toolCall.status === 'running') {
        part.toolCall.status = 'complete';
      }
      // Sub-calls are always closed (if we're starting a new top-level call,
      // any nested running sub-calls of other tools are definitely done).
      if (part.toolCall.toolId !== exceptToolId) {
        closeRunningRecursive(part.toolCall.subCalls);
      }
    }
  }
};

/**
 * Walk every message's parts and mark any still-running tool call as complete.
 * Strands' current adapter doesn't emit TOOL_CALL_END, so on run boundaries we
 * close them out locally — this also walks sub-calls so nested tool chains
 * close cleanly.
 */
const completeStreamingArtifacts = (state: EpiChatState) => {
  for (const message of state.messages) {
    if (message.status === 'streaming') {
      message.status = 'complete';
    }
    for (const part of message.parts) {
      if (part.kind === 'tool_call') {
        if (part.toolCall.status === 'running') part.toolCall.status = 'complete';
        closeRunningRecursive(part.toolCall.subCalls);
      }
    }
  }
};

/**
 * Close any in-flight verification executions when the run terminates without
 * an EXECUTION_ENDED event. Without this, an aborted/disconnected/errored run
 * leaves the latest execution row stuck on `pending`, which renders as a
 * forever-spinning "running" in the Verification tab — a stale lie that hides
 * the fact that nothing is actually running on the server.
 *
 * Marks status='error' (not 'failed') because we don't know what the SQL
 * outcome would have been; it's a stream-closure event, not a SQL failure.
 * The summary copy explains the situation in plain English so the epi knows
 * to retry rather than read the absent results as authoritative.
 */
const closePendingExecutions = (state: EpiChatState, reason: 'ended' | 'cancelled' | 'errored') => {
  const summary =
    reason === 'errored'
      ? 'Did not complete — the agent run errored before results came back.'
      : reason === 'cancelled'
      ? 'Did not complete — the run was cancelled.'
      : 'Did not complete — the run ended before results came back.';
  const completedAt = new Date().toISOString();
  for (const artifact of state.artifacts) {
    if (!artifact.executions) continue;
    for (const execution of artifact.executions) {
      if (execution.status === 'pending') {
        execution.status = 'error';
        execution.summary = execution.summary ?? summary;
        execution.completedAt = completedAt;
      }
    }
  }
};

/** A user turn's parts, files first. Shared by the live send and the echo. */
const userMessageParts = (text: string, attachments?: EpiAttachment[]): EpiMessagePart[] => [
  ...(attachments ?? []).map((attachment): EpiMessagePart => ({ kind: 'attachment', attachment })),
  { kind: 'text', text },
];

/**
 * Flip the checkpoint (or av_selection_checkpoint) card matching
 * ``checkpointId`` into its resolved state — set ``answered`` and record the
 * user's ``answerText`` so the card renders the chip + answer with no buttons.
 *
 * Shared by ``userMessageSent`` (live click) and ``userMessageEchoed``
 * (replay / second-tab): both must resolve the card identically, so a single
 * durable ``user_message`` ui# event carrying a ``checkpointId`` reconstructs
 * the same resolved card on reload as the live click produced. Idempotent —
 * safe to re-apply on a duplicate echo. Matches every part across all
 * messages so it works whether the card lives on an assistant message or a
 * standalone checkpoint bubble.
 */
const applyCheckpointAnswer = (state: EpiChatState, checkpointId: string, answerText: string) => {
  for (const message of state.messages) {
    for (const part of message.parts) {
      if (
        (part.kind === 'checkpoint' || part.kind === 'av_selection_checkpoint') &&
        part.checkpoint.checkpointId === checkpointId
      ) {
        part.checkpoint.answered = true;
        part.checkpoint.answerText = answerText;
      }
    }
  }
};

/**
 * Flip the clarifying-question card matching ``questionId`` into its resolved
 * state — set ``answered`` so ``MessageBubble`` renders it as prose instead of
 * the interactive card, and record the user's ``answerText`` alongside the
 * checkpoint cards' own resolved field.
 *
 * OR-6341: the symmetric completion of OR-6262's ``applyCheckpointAnswer`` for
 * clarifying questions. Shared by ``userMessageSent`` (live click) and
 * ``userMessageEchoed`` (replay / second-tab): both must resolve the card
 * identically, so a single durable ``user_message`` ui# event carrying a
 * ``questionId`` reconstructs the same resolved card on reload as the live
 * answer produced. Idempotent — safe to re-apply on a duplicate echo. Matches
 * every part across all messages.
 */
const applyQuestionAnswer = (state: EpiChatState, questionId: string, answerText: string) => {
  for (const message of state.messages) {
    for (const part of message.parts) {
      if (part.kind === 'clarifying_question' && part.question.questionId === questionId) {
        part.question.answered = true;
        part.question.answerText = answerText;
      }
    }
  }
};

const slice = createSlice({
  name: 'epiChat',
  initialState: initialEpiChatState,
  reducers: {
    sessionEstablished(state, action: PayloadAction<{ sessionId: string }>) {
      state.sessionId = action.payload.sessionId;
      state.error = null;
    },
    /** Composer-side ``send('subscribe')`` just fired — show the loading
     *  affordance until the backend acks ``ready`` or hydrates a snapshot, and
     *  re-arm the submit gate until this subscribe's ``ready`` confirms the
     *  authoritative run state. */
    subscribeStarted(state) {
      state.subscribing = true;
      state.subscribeAcked = false;
    },
    /**
     * Backend refused the subscribe with a *retryable* code
     * (``SUBSCRIBE_UNAVAILABLE`` — a transient/infra read failure). Bump the
     * nonce so the page hook schedules a bounded-backoff re-subscribe instead
     * of dead-ending on the terminal "no access" panel. Deliberately leaves
     * ``error``/``runState`` untouched.
     */
    subscribeRetryRequested(state) {
      state.subscribeRetryNonce += 1;
    },
    /**
     * Backend's ``STATUS_UPDATE { status: 'ready' }`` arrived — the subscribe
     * handshake is complete. Clears the loading affordance and opens the submit
     * gate, applying the run state the ack carries so both land atomically:
     *
     *  - ``inflightRun`` present → a request is still processing; flip straight
     *    to the in-flight (Stop) state. Without this the composer would briefly
     *    show Send between ``ready`` and the separate run re-announce.
     *  - ``welcome`` false → no greeting will stream (resumed session, or a
     *    gen_3 chat where the welcome is suppressed). Clear ``initializing`` so
     *    the composer enables; the FE default of ``true`` anticipates a welcome
     *    that, here, never comes. When ``welcome`` is true we leave
     *    ``initializing`` set — the welcome-bracket events own its lifecycle.
     */
    subscribeReady(
      state,
      action: PayloadAction<{
        welcome: boolean;
        inflightRun: { runId: string } | null;
        interruptedRun?: { runId: string; retryable: boolean } | null;
      }>
    ) {
      state.subscribing = false;
      state.subscribeAcked = true;
      if (!action.payload.welcome) {
        state.initializing = false;
      }
      if (action.payload.inflightRun) {
        state.runState = 'streaming';
        state.runId = action.payload.inflightRun.runId;
        state.error = null;
        state.interruptedRun = null;
      } else if (action.payload.interruptedRun) {
        // Orphaned-run (dead-lease) state carried on the ack. Applied here —
        // the LAST event of the subscribe handshake — so a snapshot's
        // ``hydrated`` reset (which nulls ``interruptedRun``) can't clobber the
        // Retry card on a multi-subscribe reconnect. Re-assert defensively.
        state.runState = 'idle';
        state.runId = null;
        state.interruptedRun = {
          runId: action.payload.interruptedRun.runId,
          retryable: action.payload.interruptedRun.retryable,
        };
      }
    },
    runStarted(state, action: PayloadAction<{ runId: string }>) {
      state.runState = 'streaming';
      state.runId = action.payload.runId;
      state.error = null;
      // Backstop the submit gate: an authoritative run-lifecycle event means
      // we know the run state even if the ``ready`` ack was dropped, so never
      // leave the composer wedged behind ``subscribeAcked``.
      state.subscribeAcked = true;
      // A fresh run supersedes any interrupted-run notice. Covers both the
      // post-retry RUN_STARTED and a ``kind:'run' status:'streaming'``
      // re-announce for a run that genuinely executes on another pod.
      state.interruptedRun = null;
    },
    runFinished(state) {
      state.runState = 'idle';
      state.runId = null;
      state.initializing = false;
      state.subscribeAcked = true;
      completeStreamingArtifacts(state);
      closePendingExecutions(state, 'ended');
    },
    // OR-6348: the socket send behind ``sendMessage``'s optimistic
    // ``runStarted`` dispatch has no ack to ``.catch()`` — ``NOT_SUBSCRIBED``
    // is the one ERROR code that otherwise fully no-ops, which would leave
    // "RA is working" stuck forever if the optimistic sentinel was showing
    // when it arrives. Gated on the sentinel so an unrelated NOT_SUBSCRIBED
    // (or one that arrives after a real run already superseded the
    // sentinel) can't clobber state it doesn't own.
    optimisticSendRejected(state) {
      if (state.runId !== OPTIMISTIC_SEND_RUN_ID) return;
      state.runState = 'idle';
      state.runId = null;
      state.initializing = false;
    },
    runCancelled(state) {
      state.runState = 'idle';
      state.initializing = false;
      completeStreamingArtifacts(state);
      closePendingExecutions(state, 'cancelled');
    },
    runErrored(state, action: PayloadAction<{ message: string }>) {
      state.runState = 'error';
      state.error = action.payload.message;
      state.initializing = false;
      state.subscribeAcked = true;
      const last = state.messages[state.messages.length - 1];
      if (last && last.status === 'streaming') {
        last.status = 'error';
      }
      closePendingExecutions(state, 'errored');
    },
    /**
     * Server says a previous run is still going (RUN_LEASE_HELD_ELSEWHERE
     * or RUN_IN_PROGRESS). Surface the message as an error toast but keep
     * the runState in `streaming` so the existing Stop button is visible —
     * the user's recovery path is to cancel the wedged run, not to be told
     * "your message was rejected" with no recourse. The BE `on_cancel`
     * handler ignores the run_id payload and cancels whichever task is
     * actually alive on the session record, so a Stop click here works
     * even though our local runId is stale.
     */
    runBusyNoticeReceived(state, action: PayloadAction<{ message: string }>) {
      state.error = action.payload.message;
      state.runState = 'streaming';
      state.initializing = false;
    },
    /**
     * Non-blocking inline notice (a backend WARNING, e.g. the stall nudge).
     * Appends to the live streaming
     * assistant message when one exists so it renders in order with the
     * agent's prose; otherwise lands as a standalone ``system`` message.
     * Deliberately does NOT touch ``runState``/``error`` — the run continues
     * and the red Alert banner is reserved for terminal failures.
     */
    noticeReceived(
      state,
      action: PayloadAction<{ message: string; severity: 'error' | 'warning' | 'info' }>
    ) {
      const part: EpiMessagePart = {
        kind: 'notice',
        severity: action.payload.severity,
        message: action.payload.message,
      };
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant' && last.status === 'streaming') {
        last.parts.push(part);
        return;
      }
      state.messages.push({
        id: nanoid(),
        role: 'system',
        parts: [part],
        status: 'complete',
        timestamp: new Date().toISOString(),
      });
    },
    /**
     * The run's pod died mid-flight (lease expired on a cross-pod reconnect;
     * ``STATUS_UPDATE { kind: 'run_interrupted' }``). There is no live task to
     * tail, so flip ``runState`` off ``streaming`` — this kills the frozen
     * "Agent is working…" dots AND lets ``sendMessage`` actually fire a new
     * run on Retry (it queues silently while ``streaming``). Record the
     * interrupted run so the transcript renders a Retry card. Close out any
     * orphaned streaming message / running tool / pending execution the same
     * way ``staleRunCleared`` does so nothing spins forever. ``error`` is left
     * untouched — the card is the affordance, not the red Alert banner.
     */
    runInterrupted(state, action: PayloadAction<{ runId: string; retryable: boolean }>) {
      state.runState = 'idle';
      state.runId = null;
      state.initializing = false;
      state.subscribeAcked = true;
      state.interruptedRun = {
        runId: action.payload.runId,
        retryable: action.payload.retryable,
      };
      for (const message of state.messages) {
        if (message.status === 'streaming') message.status = 'error';
        for (const part of message.parts) {
          if (part.kind === 'tool_call') {
            if (part.toolCall.status === 'running') part.toolCall.status = 'error';
            closeRunningRecursive(part.toolCall.subCalls);
          }
        }
      }
      for (const artifact of state.artifacts) {
        if (artifact.status === 'streaming') artifact.status = 'complete';
      }
      closePendingExecutions(state, 'errored');
    },
    userMessageSent(
      state,
      action: PayloadAction<{
        text: string;
        id?: string;
        questionId?: string;
        checkpointId?: string;
        attachments?: EpiAttachment[];
      }>
    ) {
      // Sending (including a Retry click, which routes through this action)
      // supersedes any interrupted-run notice immediately, before the new
      // run's RUN_STARTED lands.
      state.interruptedRun = null;
      // The id is client-minted by the sender and echoed back by the server
      // (kind='user_message') so other tabs viewing this session can render
      // the same bubble live; ``userMessageEchoed`` dedupes on it. Falls back
      // to a fresh id for callers that don't supply one (no echo correlation,
      // single-tab behaviour unchanged).
      state.messages.push({
        id: action.payload.id ?? nanoid(),
        role: 'user',
        parts: userMessageParts(action.payload.text, action.payload.attachments),
        status: 'complete',
        timestamp: new Date().toISOString(),
      });
      if (action.payload.questionId) {
        applyQuestionAnswer(state, action.payload.questionId, action.payload.text);
      }
      if (action.payload.checkpointId) {
        applyCheckpointAnswer(state, action.payload.checkpointId, action.payload.text);
      }
    },
    // Multi-tab fan-out: the server broadcasts a ``kind='user_message'`` echo
    // (carrying the sender's client-minted id) to every tab in the session
    // room. The sender already rendered this bubble optimistically via
    // ``userMessageSent`` with the same id, so it dedupes to a no-op here;
    // OTHER tabs viewing the same session have no such bubble and render it.
    // Deduping on id also makes a buffered-event resume idempotent.
    userMessageEchoed(
      state,
      action: PayloadAction<{
        id: string;
        text: string;
        questionId?: string;
        checkpointId?: string;
        attachments?: EpiAttachment[];
      }>
    ) {
      // Resolve the answered gate card first, before the dedupe guard: this echo
      // is the ONLY signal that flips the card on a second tab or a full reload
      // (the durable ``user_message`` ui# event carries the ``questionId`` /
      // ``checkpointId`` the live ``userMessageSent`` answer had). Applying it
      // ahead of the guard also re-asserts the resolved state on a duplicate
      // echo, which is idempotent (see ``applyQuestionAnswer`` /
      // ``applyCheckpointAnswer``).
      //
      // OR-6341: clarifying-question resolution, symmetric with OR-6262's
      // checkpoint resolution — without it an answered clarifying-question card
      // re-appears unresolved on refresh / second tab.
      if (action.payload.questionId) {
        applyQuestionAnswer(state, action.payload.questionId, action.payload.text);
      }
      if (action.payload.checkpointId) {
        applyCheckpointAnswer(state, action.payload.checkpointId, action.payload.text);
      }
      if (state.messages.some((message) => message.id === action.payload.id)) return;
      state.messages.push({
        id: action.payload.id,
        role: 'user',
        parts: userMessageParts(action.payload.text, action.payload.attachments),
        status: 'complete',
        timestamp: new Date().toISOString(),
      });
    },
    textChunkReceived(state, action: PayloadAction<{ runId: string; content: string }>) {
      // The model is speaking again — any preceding tool call must have finished.
      closeOtherRunningTools(state);
      const message = ensureAssistantMessage(state, action.payload.runId);
      appendTextPart(message, action.payload.content);
    },
    reasoningReceived(state, action: PayloadAction<{ runId: string; text: string }>) {
      const message = ensureAssistantMessage(state, action.payload.runId);
      const last = message.parts[message.parts.length - 1];
      if (last && last.kind === 'reasoning') {
        last.text += action.payload.text;
        return;
      }
      message.parts.push({ kind: 'reasoning', text: action.payload.text });
    },
    /**
     * Sub-agent reasoning text — STATUS_UPDATE event with a parentEventId
     * pointing at an orchestrator-level tool call. Appends to that
     * parent's ``internalLog`` so the drill-down renders reasoning
     * interleaved with the tool calls it narrates. Never lands on the
     * orchestrator's assistant bubble.
     *
     * Coalesces consecutive reasoning chunks into one log entry so the
     * timeline doesn't fragment into one row per token. A new entry
     * starts the moment a tool_call lands between chunks.
     */
    subAgentReasoningReceived(
      state,
      action: PayloadAction<{ parentToolId: string; text: string; agentId?: string }>
    ) {
      const parent = findToolPartAnywhere(state, action.payload.parentToolId);
      if (!parent) return;
      const log = (parent.internalLog ??= []);
      const last = log[log.length - 1];
      if (last && last.kind === 'reasoning') {
        last.text += action.payload.text;
        return;
      }
      const entry: EpiInternalLogEntry = {
        kind: 'reasoning',
        id: nanoid(),
        text: action.payload.text,
      };
      log.push(entry);
      void action.payload.agentId; // Reserved for per-entry agent labelling.
    },
    toolCallStarted(
      state,
      action: PayloadAction<{
        runId: string;
        toolId: string;
        toolName: string;
        friendlyLabel: string;
        initialArgs?: Record<string, unknown>;
        /**
         * Sub-agent tool calls carry the orchestrator tool_use_id this call
         * nests under (OR-5964 envelope `parentEventId`). The reducer
         * attaches these to the parent's `subCalls` array so the UI can
         * render the sub-agent's internal steps inside its delegating
         * tool group.
         */
        parentToolId?: string;
        /**
         * Identifier of the agent that emitted this tool call (OR-5964
         * envelope `agentId`). Top-level tools carry `orchestrator`;
         * sub-agent internals carry the sub-agent name (e.g. `av_drafter`).
         */
        agentId?: string;
        /** OR-5964 routing surface — defaults applied at the backend. */
        surface?: EpiSurface;
      }>
    ) {
      // Suppress tool-call cards that have a dedicated UI surface (e.g. clarifying
      // questions render as ClarifyingQuestionCard).
      if (SUPPRESSED_TOOL_CARDS.has(action.payload.toolName)) {
        return;
      }
      // Idempotent: Strands emits `current_tool_use` events per streamed delta of
      // the model's tool-call JSON, so the same toolUseId arrives repeatedly —
      // each carrying a longer prefix of the parsed args. Don't create a new
      // card on re-entry, but DO merge in any newly-parsed args so the inline
      // label upgrades from "Search Catalog" to "Search Catalog for 'asthma'"
      // the moment the JSON becomes parseable.
      const existing = findToolPartAnywhere(state, action.payload.toolId);
      if (existing) {
        if (action.payload.initialArgs) {
          existing.args = { ...existing.args, ...action.payload.initialArgs };
        }
        return;
      }
      // A new tool is starting — close any previously-running tool calls.
      closeOtherRunningTools(state, action.payload.toolId);
      const toolCall: EpiToolCall = {
        toolId: action.payload.toolId,
        toolName: action.payload.toolName,
        friendlyLabel: action.payload.friendlyLabel,
        args: action.payload.initialArgs ?? {},
        status: 'running',
        agentId: action.payload.agentId,
        surface: action.payload.surface,
        subCalls: [],
        internalLog: [],
      };

      if (action.payload.parentToolId) {
        const parent = findToolPartAnywhere(state, action.payload.parentToolId);
        if (parent) {
          (parent.subCalls ??= []).push(toolCall);
          // Mirror into the heterogeneous timeline. ``internalLog`` and
          // ``subCalls`` are both populated so existing render paths
          // (recursive ToolCallCard) keep working while the new timeline
          // path renders the chronological mix of reasoning + tool calls.
          (parent.internalLog ??= []).push({
            kind: 'tool_call',
            toolId: toolCall.toolId,
          });
          return;
        }
        // Parent not found — fall through and attach to the message tree as
        // a sibling rather than dropping the event on the floor.
      }

      const message = ensureAssistantMessage(state, action.payload.runId);
      message.parts.push({ kind: 'tool_call', toolCall });
    },
    toolCallArgs(
      state,
      action: PayloadAction<{
        runId: string;
        toolId: string;
        args: Record<string, unknown>;
      }>
    ) {
      const existing = findToolPartAnywhere(state, action.payload.toolId);
      if (existing) {
        existing.args = { ...existing.args, ...action.payload.args };
      }
    },
    toolCallEnded(
      state,
      action: PayloadAction<{
        runId: string;
        toolId: string;
        result?: unknown;
        error?: string;
      }>
    ) {
      const existing = findToolPartAnywhere(state, action.payload.toolId);
      if (existing) {
        existing.status = action.payload.error ? 'error' : 'complete';
        existing.result = action.payload.result;
      }
    },
    /**
     * OR-6324/OR-6328 — a best-effort tool failure reported by the backend
     * (``STATUS_UPDATE status=tool_failure_best_effort``). Attach the
     * plain-language note to the affected call so the working-state row can
     * show it. Deliberately does NOT set ``status='error'``: the assistant
     * continued without the result, and the group header's last-call-error
     * rule must not treat the turn as failed. Unknown ``toolId`` (event
     * raced ahead of TOOL_CALL_START, or the row was suppressed) is dropped
     * — the durable ui# event and the telemetry WARNING remain the source
     * of truth for detection.
     */
    toolFailureNoted(state, action: PayloadAction<{ toolId: string; message: string }>) {
      const existing = findToolPartAnywhere(state, action.payload.toolId);
      if (existing) {
        existing.bestEffortFailureNote = action.payload.message;
      }
    },
    clarifyingQuestionReceived(
      state,
      action: PayloadAction<{ runId: string; question: EpiClarifyingQuestion }>
    ) {
      // Idempotent on questionId — mirrors checkpointReceived /
      // avSelectionCheckpointReceived. The durable ui# stream is re-folded on
      // every reconnect (a backgrounded tab can trigger several in a row) and
      // the backend may re-emit an INPUT_REQUEST on a retry; without this guard
      // each would push a duplicate card. Safe against collapsing two distinct
      // questions: replay reconstructs questionId from the stable per-tool-call
      // ``tool_use_id`` (see session_replay._clarifying_question_from_args), so
      // distinct clarifications keep distinct ids. Skip so an already-answered
      // card's state is never clobbered by a late duplicate.
      for (const message of state.messages) {
        for (const part of message.parts) {
          if (
            part.kind === 'clarifying_question' &&
            part.question.questionId === action.payload.question.questionId
          ) {
            return;
          }
        }
      }
      const message = ensureAssistantMessage(state, action.payload.runId);
      const part: EpiMessagePart = {
        kind: 'clarifying_question',
        question: action.payload.question,
      };
      message.parts.push(part);
      state.runState = 'awaiting_input';
    },
    checkpointReceived(state, action: PayloadAction<{ runId: string; checkpoint: EpiCheckpoint }>) {
      // Close any running tool that was finishing up (e.g. the `checkpoint`
      // tool itself — suppressed from view but still in the stream) so the
      // card appears as the active focus.
      closeOtherRunningTools(state);
      // Idempotent on checkpoint_id: the backend may fire a duplicate sink
      // event on retries. Don't push a second card.
      for (const message of state.messages) {
        for (const part of message.parts) {
          if (
            part.kind === 'checkpoint' &&
            part.checkpoint.checkpointId === action.payload.checkpoint.checkpointId
          ) {
            return;
          }
        }
      }
      const message = ensureAssistantMessage(state, action.payload.runId);
      message.parts.push({ kind: 'checkpoint', checkpoint: action.payload.checkpoint });
      state.runState = 'awaiting_input';
    },
    avSelectionCheckpointReceived(
      state,
      action: PayloadAction<{ runId: string; checkpoint: AvSelectionCheckpoint }>
    ) {
      closeOtherRunningTools(state);
      // Idempotent on checkpointId.
      for (const message of state.messages) {
        for (const part of message.parts) {
          if (
            part.kind === 'av_selection_checkpoint' &&
            part.checkpoint.checkpointId === action.payload.checkpoint.checkpointId
          ) {
            return;
          }
        }
      }
      const message = ensureAssistantMessage(state, action.payload.runId);
      message.parts.push({
        kind: 'av_selection_checkpoint',
        checkpoint: action.payload.checkpoint,
      });
      state.runState = 'awaiting_input';
    },
    messageQueued(state, action: PayloadAction<EpiChatSendPayload>) {
      state.queue.push(action.payload);
    },
    messageDequeued(state, action: PayloadAction<{ index: number }>) {
      state.queue.splice(action.payload.index, 1);
    },
    queueFlushed(state) {
      state.queue = [];
    },
    artifactStarted(
      state,
      action: PayloadAction<
        { id: string; artifactType: EpiArtifact['artifactType']; dataElement: string } & Partial<
          Pick<
            EpiArtifact,
            | 'name'
            | 'kind'
            | 'description'
            | 'explainMarkdown'
            | 'clinicalExplanation'
            | 'coreLabel'
            | 'outputValueType'
            | 'outputValues'
            | 'format'
            | 'conceptGroupIds'
            | 'sources'
            | 'knowledgeApplied'
            | 'conceptBindings'
            | 'conceptMappings'
            | 'designDecisions'
            | 'artifactStatus'
            | 'validationBlocking'
            | 'scope'
            | 'latestVersionId'
            | 'codes'
            | 'definition'
            | 'usage'
            | 'executions'
          >
        >
      >
    ) {
      // Stable per-slug artifact ids mean a re-emit on a later turn should
      // refresh the existing card in place rather than push a duplicate. The
      // SQL body is reset here so the streaming chunks land into a fresh
      // buffer; manifest fields overwrite to reflect the latest version.
      // Verification runs and result previews accumulate across re-emits
      // (they're additive surfaces, not part of the manifest), so they're
      // preserved from the existing artifact when one is found.
      const existing = state.artifacts.find((a) => a.id === action.payload.id);
      const next: EpiArtifact = {
        id: action.payload.id,
        artifactType: action.payload.artifactType,
        dataElement: action.payload.dataElement,
        content: '',
        status: 'streaming',
        updatedAt: new Date().toISOString(),
        name: action.payload.name,
        kind: action.payload.kind,
        description: action.payload.description,
        explainMarkdown: action.payload.explainMarkdown,
        clinicalExplanation: action.payload.clinicalExplanation,
        coreLabel: action.payload.coreLabel,
        outputValueType: action.payload.outputValueType,
        outputValues: action.payload.outputValues,
        format: action.payload.format,
        conceptGroupIds: action.payload.conceptGroupIds,
        sources: action.payload.sources,
        knowledgeApplied: action.payload.knowledgeApplied,
        conceptBindings: action.payload.conceptBindings,
        conceptMappings: action.payload.conceptMappings,
        designDecisions: action.payload.designDecisions,
        artifactStatus: action.payload.artifactStatus,
        validationBlocking: action.payload.validationBlocking,
        scope: action.payload.scope,
        latestVersionId: action.payload.latestVersionId,
        codes: action.payload.codes ?? existing?.codes,
        definition: action.payload.definition ?? existing?.definition,
        usage: action.payload.usage ?? existing?.usage,
        // ARTIFACT_START may carry the full executions audit trail (e.g.
        // when re-emitted on a resume). When absent, preserve whatever's
        // already in state — execution events are additive surfaces, not
        // part of the manifest core.
        executions: action.payload.executions ?? existing?.executions,
        results: existing?.results,
      };
      if (existing) {
        Object.assign(existing, next);
      } else {
        state.artifacts.push(next);
      }
      // Auto-focus removed deliberately: the user is the only one who
      // navigates the drawer. A new artifact (or a re-emit of an old one)
      // does NOT yank `activeArtifactId` away from whatever the user is
      // currently reading. The artifact-ref card pushed onto the chat
      // below is the notification; the user clicks it to focus.
      // Drop a clickable artifact-ref card into the streaming assistant
      // message so chat shows WHEN the artifact was created — without
      // the orchestrator having to dump prose for it. Click handler in
      // the message renderer dispatches `artifactSelected` to focus the
      // drawer. Idempotent on artifactId so re-emits (a later turn
      // updating the same slug, a resume replay) don't push duplicate
      // cards; the existing card just refreshes its mirrored fields.
      //
      // Gated on `kind` so we don't emit a card for the bare template-
      // fill commit that lands `avs/<slug>/draft.sql` before the manifest
      // is written. The namespace's `_emit_artifact_for_slug` only
      // populates `kind` (and the rest of manifest_meta) when
      // `manifest.json` is present alongside `draft.sql` — so `kind`
      // being set is the wire-level "the agent finalized this" signal.
      // Showing a clickable card during the template-fill phase pointed
      // the epi at half-rendered SQL, which read as "draft is ready"
      // before the agent had even drafted the annotations.
      const refExists = state.messages.some((m) =>
        m.parts.some(
          (p) => p.kind === 'artifact_ref' && p.artifactRef.artifactId === action.payload.id
        )
      );
      // OR-6089: when a `resource_ref` for the same analytical variable is already in
      // the stream, skip pushing the legacy `artifact_ref` — the two
      // cards otherwise stack visibly (the user reports seeing both
      // in chat). The `resource_ref` is the canonical surface for
      // the gen_3 project-page flow: click routes to the variable
      // detail page, status pill mirrors the ratchet. The
      // `artifact_ref` was the only card before OR-6089 and is
      // still emitted unconditionally by the namespace's mtime-poll
      // when the manifest commits — we just suppress its chat
      // affordance here when the new card is already doing the
      // notifying. The artifact itself stays in ``state.artifacts``
      // (drawer + draft surface still need it).
      //
      // Match by slug, with two strategies covering the order
      // race-window: BEFORE the ratchet emit, the resource_ref
      // carries the slug only via ``hintArgs.analytical_variable_name`` (set at
      // ``draft_analytical_variable`` entry); AFTER the ratchet, the slug is on
      // ``resourceId='av.<slug>'``. Either matches.
      const slug = action.payload.name;
      const resourceCardForSlugExists =
        slug !== undefined &&
        state.messages.some((m) =>
          m.parts.some((p) => {
            if (p.kind !== 'resource_ref') return false;
            if (p.resourceRef.resourceKind !== 'analytical_variable') return false;
            if (p.resourceRef.resourceId === `av.${slug}`) return true;
            const hintSlug = p.resourceRef.hintArgs?.['analytical_variable_name'];
            return typeof hintSlug === 'string' && hintSlug === slug;
          })
        );
      if (!refExists && !resourceCardForSlugExists && action.payload.kind) {
        const lastMessage = state.messages[state.messages.length - 1];
        const message =
          lastMessage && lastMessage.role === 'assistant' && lastMessage.status === 'streaming'
            ? lastMessage
            : (() => {
                const m: EpiMessage = {
                  id: nanoid(),
                  role: 'assistant',
                  parts: [],
                  status: 'streaming',
                  timestamp: new Date().toISOString(),
                };
                state.messages.push(m);
                return m;
              })();
        message.parts.push({
          kind: 'artifact_ref',
          artifactRef: {
            artifactId: action.payload.id,
            dataElement: action.payload.dataElement,
            name: action.payload.name,
            kind: action.payload.kind,
            createdAt: new Date().toISOString(),
          },
        });
      }
    },
    artifactScopeChanged(
      state,
      action: PayloadAction<{ id: string; scope: EpiArtifact['scope']; latestVersionId?: string }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.id);
      if (!artifact) return;
      artifact.scope = action.payload.scope;
      if (action.payload.latestVersionId !== undefined) {
        artifact.latestVersionId = action.payload.latestVersionId;
      }
      artifact.updatedAt = new Date().toISOString();
    },
    artifactChunkReceived(state, action: PayloadAction<{ id: string; content: string }>) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.id);
      if (!artifact) return;
      artifact.content += action.payload.content;
      artifact.updatedAt = new Date().toISOString();
    },
    artifactCompleted(state, action: PayloadAction<{ id: string }>) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.id);
      if (!artifact) return;
      artifact.status = 'complete';
      artifact.updatedAt = new Date().toISOString();
    },
    /**
     * OR-6089: add or refresh a project-resource chat card.
     *
     * Sibling to ``artifactStarted``: that one rides the per-session
     * ``artifacts[]`` and drives the in-chat drawer; this one is for
     * resources rendered by TanStack-driven detail pages (cohorts, analytical variables
     * post-finalize, concept groups). The card lives only as a
     * ``resource_ref`` message-part — there is no parallel collection
     * in slice state, by design: the source of truth is server state
     * the page refetches via ``RESOURCE_UPDATED``.
     *
     * Identity matcher: ``(clientId || resourceId)`` collision counts as
     * the same logical card. The analytical variable pipeline emits successive cards
     * that share a slot — first keyed by ``clientId`` only, later
     * carrying both ``clientId`` and ``resourceId`` (the ratchet emit).
     * For each successive emit we keep ``createdAt`` from the existing
     * card and bump ``updatedAt`` so consumers can pulse on refresh.
     *
     * OR-6344: a re-emit that lands on a LATER streaming turn moves the single
     * card down onto that turn (detach + re-attach) instead of refreshing it
     * in place on its original message — otherwise a card first shown on turn 1
     * stays stranded up-scroll while later turns (an AV edit, a cohort criteria
     * refinement) render only their Approve prompt with no card above it.
     *
     * When a brand-new card arrives we append the ``resource_ref`` part
     * to the current streaming assistant message — same fallback path
     * the artifact-ref reducer uses for replay safety when no
     * streaming bubble is open.
     */
    resourceCardReceived(state, action: PayloadAction<EpiResourceRef>) {
      const incoming = action.payload;
      const matches = (ref: EpiResourceRef): boolean => {
        if (incoming.clientId && ref.clientId && incoming.clientId === ref.clientId) {
          return true;
        }
        if (incoming.resourceId && ref.resourceId && incoming.resourceId === ref.resourceId) {
          return true;
        }
        return false;
      };
      // OR-6089: when the incoming card is for an analytical variable, drop any
      // legacy ``artifact_ref`` for the same slug. The two cards
      // describe the same artifact but with different click targets
      // (drawer vs route); the gen_3 project-page flow wants the
      // route-navigating ``resource_ref`` to be the sole chat
      // affordance. ``artifactStarted`` already guards the forward
      // race (resource_ref arrives first); this cleanup handles the
      // reverse race window (artifact_ref arrived first because the
      // namespace's mtime-poll fired before draft_analytical_variable's entry emit
      // landed on the FE).
      if (incoming.resourceKind === 'analytical_variable') {
        const incomingSlugs = new Set<string>();
        if (incoming.resourceId?.startsWith('av.')) {
          incomingSlugs.add(incoming.resourceId.slice('av.'.length));
        }
        const hintSlug = incoming.hintArgs?.['analytical_variable_name'];
        if (typeof hintSlug === 'string') incomingSlugs.add(hintSlug);
        if (incomingSlugs.size > 0) {
          for (const message of state.messages) {
            message.parts = message.parts.filter((p) => {
              if (p.kind !== 'artifact_ref') return true;
              const slug = p.artifactRef.name;
              return slug === undefined || !incomingSlugs.has(slug);
            });
          }
        }
      }
      // Locate the existing chip AND the message it currently lives on, so a
      // cross-turn re-emit can MOVE it to the active turn rather than only
      // refresh it in place up-scroll (OR-6344).
      let existing: EpiResourceRef | undefined;
      let existingMessage: EpiMessage | undefined;
      let existingPartIndex = -1;
      for (const message of state.messages) {
        const partIndex = message.parts.findIndex(
          (p) => p.kind === 'resource_ref' && matches(p.resourceRef)
        );
        if (partIndex !== -1) {
          const part = message.parts[partIndex];
          if (part.kind === 'resource_ref') {
            existing = part.resourceRef;
            existingMessage = message;
            existingPartIndex = partIndex;
          }
          break;
        }
      }

      // The turn the researcher is currently looking at — the live streaming
      // assistant message. Undefined once a run settles (awaiting input); we
      // never strand-move a chip onto a phantom turn in that state.
      const lastMessage = state.messages[state.messages.length - 1];
      const activeStreaming =
        lastMessage && lastMessage.role === 'assistant' && lastMessage.status === 'streaming'
          ? lastMessage
          : undefined;

      if (existing && existingMessage) {
        // Fold the incoming emit onto the retained chip: preserve the original
        // createdAt (chat ordering stays stable across refresh) and carry
        // forward whichever id was already populated so the ratchet's match
        // key never drops.
        const mergedRef: EpiResourceRef = {
          resourceKind: incoming.resourceKind,
          projectId: incoming.projectId,
          name: incoming.name,
          status: incoming.status,
          route: incoming.route,
          hintArgs: incoming.hintArgs ?? existing.hintArgs,
          resourceId: incoming.resourceId ?? existing.resourceId,
          clientId: incoming.clientId ?? existing.clientId,
          createdAt: existing.createdAt,
          updatedAt: incoming.updatedAt,
        };
        // OR-6344: the single card must ride the latest turn so it renders
        // directly above that turn's Approve prompt. A re-emit on a NEW
        // streaming turn means the chip is stranded on an earlier message
        // (e.g. a cohort refined over several turns keeps the same resourceId
        // and would otherwise refresh in place on turn 1, leaving later turns
        // card-less) — detach it and re-attach it to the active turn. A
        // same-turn re-emit (the planning→drafting→ready ratchet) has nowhere
        // to move to, so it just refreshes in place.
        if (activeStreaming && activeStreaming !== existingMessage) {
          existingMessage.parts.splice(existingPartIndex, 1);
          activeStreaming.parts.push({ kind: 'resource_ref', resourceRef: mergedRef });
        } else {
          Object.assign(existing, mergedRef);
        }
        return;
      }

      // Brand-new chip: append to the active streaming turn, or open one —
      // same replay-safe fallback the artifact-ref reducer uses when no
      // streaming bubble is live.
      const message =
        activeStreaming ??
        (() => {
          const m: EpiMessage = {
            id: nanoid(),
            role: 'assistant',
            parts: [],
            status: 'streaming',
            timestamp: new Date().toISOString(),
          };
          state.messages.push(m);
          return m;
        })();
      message.parts.push({
        kind: 'resource_ref',
        resourceRef: { ...incoming },
      });
    },
    uploadedFileReceived(state, action: PayloadAction<EpiUploadedFile>) {
      // OR-6125: append a distinct uploaded-file chip (re-downloadable) to the
      // current assistant turn. Dedup by (uploadId, fileName) so a redelivered
      // event doesn't double the chip.
      const incoming = action.payload;
      for (const message of state.messages) {
        for (const part of message.parts) {
          if (
            part.kind === 'uploaded_file' &&
            part.uploadedFile.uploadId === incoming.uploadId &&
            part.uploadedFile.fileName === incoming.fileName
          ) {
            return;
          }
        }
      }
      const lastMessage = state.messages[state.messages.length - 1];
      const message =
        lastMessage && lastMessage.role === 'assistant' && lastMessage.status === 'streaming'
          ? lastMessage
          : (() => {
              const m: EpiMessage = {
                id: nanoid(),
                role: 'assistant',
                parts: [],
                status: 'streaming',
                timestamp: new Date().toISOString(),
              };
              state.messages.push(m);
              return m;
            })();
      message.parts.push({ kind: 'uploaded_file', uploadedFile: { ...incoming } });
    },
    artifactSelected(state, action: PayloadAction<{ id: string | null }>) {
      state.activeArtifactId = action.payload.id;
      // Reset to default tab when switching artifacts so the drawer doesn't
      // open a Verification tab on an artifact that has no runs yet.
      state.activeArtifactTab = null;
    },
    artifactTabSelected(state, action: PayloadAction<{ tab: EpiArtifactTab | null }>) {
      state.activeArtifactTab = action.payload.tab;
    },
    /**
     * Begin a new audit-tracked execution against the active dataset. The
     * newest execution is unshifted to the head of `artifact.executions`
     * so the Verification tab's "Latest" view is always the first row.
     * Idempotent on `executionId` — repeated `_START` events for the same
     * id replace in place (handles retries cleanly).
     */
    executionStarted(
      state,
      action: PayloadAction<{
        artifactId: string;
        executionId: string;
        versionId?: string;
        datasetReleaseId: string;
        datasetReleaseDisplayName?: string;
        userId?: string;
        userName?: string;
        startedAt?: string;
        studyStart?: string;
        studyEnd?: string;
        indexAvSlug?: string;
        indexCalendarDate?: string;
      }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact) return;
      const execution: EpiArtifactExecution = {
        executionId: action.payload.executionId,
        versionId: action.payload.versionId,
        datasetReleaseId: action.payload.datasetReleaseId,
        datasetReleaseDisplayName: action.payload.datasetReleaseDisplayName,
        userId: action.payload.userId,
        userName: action.payload.userName,
        status: 'pending',
        startedAt: action.payload.startedAt ?? new Date().toISOString(),
        studyStart: action.payload.studyStart,
        studyEnd: action.payload.studyEnd,
        indexAvSlug: action.payload.indexAvSlug,
        indexCalendarDate: action.payload.indexCalendarDate,
        logs: '',
      };
      artifact.executions = [
        execution,
        ...(artifact.executions ?? []).filter((e) => e.executionId !== action.payload.executionId),
      ];
      artifact.updatedAt = new Date().toISOString();
    },
    /**
     * Mid-flight section update on an in-flight execution. Status stays
     * `pending` — only `executionEnded` flips it. Sections are merged
     * non-destructively: a partial that carries `technicalChecks` only
     * does NOT clear an already-set `coverage`, so out-of-order writes
     * (e.g. the validator finishes after the distribution compute has
     * already published) don't lose information. Late events that name
     * a missing executionId are ignored — happens when partial events
     * race the START on slow networks; the reducer will sync once START
     * lands and a subsequent partial fires, since the orchestrator
     * writes append-only files and the namespace re-reads them.
     */
    executionPartialReceived(
      state,
      action: PayloadAction<{
        artifactId: string;
        executionId: string;
        coverage?: EpiExecutionCoverage;
        distribution?: EpiExecutionDistribution;
        conceptUsage?: EpiExecutionConceptUsage[];
        technicalChecks?: EpiExecutionTechnicalChecks;
        attrition?: EpiAttritionStep[];
      }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact?.executions) return;
      const execution = artifact.executions.find(
        (e) => e.executionId === action.payload.executionId
      );
      if (!execution) return;
      if (action.payload.coverage !== undefined) {
        execution.coverage = action.payload.coverage;
      }
      if (action.payload.distribution !== undefined) {
        execution.distribution = action.payload.distribution;
      }
      if (action.payload.conceptUsage !== undefined) {
        execution.conceptUsage = action.payload.conceptUsage;
      }
      if (action.payload.technicalChecks !== undefined) {
        execution.technicalChecks = action.payload.technicalChecks;
      }
      if (action.payload.attrition !== undefined) {
        execution.attrition = action.payload.attrition;
      }
      artifact.updatedAt = new Date().toISOString();
    },
    executionLogChunkReceived(
      state,
      action: PayloadAction<{ artifactId: string; executionId: string; content: string }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact?.executions) return;
      const execution = artifact.executions.find(
        (e) => e.executionId === action.payload.executionId
      );
      if (!execution) return;
      execution.logs += action.payload.content;
      artifact.updatedAt = new Date().toISOString();
    },
    executionEnded(
      state,
      action: PayloadAction<{
        artifactId: string;
        executionId: string;
        status: 'passed' | 'failed' | 'error' | 'empty';
        summary?: string;
        resultHash?: string;
        computeMode?: 'fresh' | 'cached';
        coverage?: EpiExecutionCoverage;
        distribution?: EpiExecutionDistribution;
        conceptUsage?: EpiExecutionConceptUsage[];
        technicalChecks?: EpiExecutionTechnicalChecks;
        attrition?: EpiAttritionStep[];
        errors?: { message: string; line?: number }[];
      }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact?.executions) return;
      const execution = artifact.executions.find(
        (e) => e.executionId === action.payload.executionId
      );
      if (!execution) return;
      execution.status = action.payload.status;
      execution.summary = action.payload.summary;
      execution.resultHash = action.payload.resultHash;
      execution.computeMode = action.payload.computeMode;
      execution.coverage = action.payload.coverage;
      execution.distribution = action.payload.distribution;
      execution.conceptUsage = action.payload.conceptUsage;
      execution.technicalChecks = action.payload.technicalChecks;
      execution.attrition = action.payload.attrition;
      execution.errors = action.payload.errors;
      execution.completedAt = new Date().toISOString();
      artifact.updatedAt = new Date().toISOString();
    },
    /**
     * Begin a new result preview. Like verification runs, the newest is
     * unshifted to the head of the list so the live tail is on top. Rows
     * append in place via {@link resultsRowsAppended} until
     * {@link resultsCompleted}. Idempotent on resultId.
     */
    resultsStarted(
      state,
      action: PayloadAction<{
        artifactId: string;
        resultId: string;
        columns: string[];
        rowCount?: number;
        summaryStats?: { label: string; value: string }[];
        computedAt?: string;
      }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact) return;
      const result: EpiArtifactResult = {
        resultId: action.payload.resultId,
        columns: action.payload.columns,
        rows: [],
        rowCount: action.payload.rowCount,
        summaryStats: action.payload.summaryStats,
        status: 'streaming',
        computedAt: action.payload.computedAt ?? new Date().toISOString(),
      };
      artifact.results = [
        result,
        ...(artifact.results ?? []).filter((r) => r.resultId !== action.payload.resultId),
      ];
      artifact.updatedAt = new Date().toISOString();
    },
    resultsRowsAppended(
      state,
      action: PayloadAction<{
        artifactId: string;
        resultId: string;
        rows: Array<Array<string | number | null>>;
      }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact?.results) return;
      const result = artifact.results.find((r) => r.resultId === action.payload.resultId);
      if (!result) return;
      result.rows.push(...action.payload.rows);
      artifact.updatedAt = new Date().toISOString();
    },
    resultsCompleted(
      state,
      action: PayloadAction<{ artifactId: string; resultId: string; rowCount?: number }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact?.results) return;
      const result = artifact.results.find((r) => r.resultId === action.payload.resultId);
      if (!result) return;
      result.status = 'complete';
      if (action.payload.rowCount !== undefined) result.rowCount = action.payload.rowCount;
      artifact.updatedAt = new Date().toISOString();
    },
    /**
     * Replace concept-group surfaces (codes, definition, usage) on an
     * existing artifact. Useful when the agent re-resolves a group or
     * fetches usage on demand without re-emitting the manifest.
     */
    conceptGroupSurfacesUpdated(
      state,
      action: PayloadAction<{
        artifactId: string;
        codes?: EpiConceptGroupCode[];
        definition?: EpiConceptGroupDefinition;
        usage?: EpiConceptGroupUsage[];
      }>
    ) {
      const artifact = state.artifacts.find((a) => a.id === action.payload.artifactId);
      if (!artifact) return;
      if (action.payload.codes !== undefined) artifact.codes = action.payload.codes;
      if (action.payload.definition !== undefined) artifact.definition = action.payload.definition;
      if (action.payload.usage !== undefined) artifact.usage = action.payload.usage;
      artifact.updatedAt = new Date().toISOString();
    },
    initializingChanged(state, action: PayloadAction<{ initializing: boolean }>) {
      state.initializing = action.payload.initializing;
    },
    /**
     * Advance the resume cursor. Dispatched once per inbound event so a
     * reconnect can ask the backend to replay from here.
     */
    eventSeqObserved(state, action: PayloadAction<{ seq: number }>) {
      if (action.payload.seq > state.lastEventSeq) {
        state.lastEventSeq = action.payload.seq;
      }
    },
    /**
     * Apply the BE-broadcast active dataset. Fires on subscribe and
     * after every ``set_active_dataset`` ack so multi-tab sessions
     * stay aligned. Empty / non-string values clear the slot — the BE
     * sends ``name: null`` when no dataset is pinned.
     */
    activeDatasetChanged(state, action: PayloadAction<{ name: string | null | undefined }>) {
      const name = action.payload.name;
      state.activeDatasetName = typeof name === 'string' && name.length > 0 ? name : null;
    },
    /**
     * Apply a BE-broadcast focus scope (OR-5963 step 2).
     *
     * Triggered by ``STATUS_UPDATE { kind: 'focus_scope' }`` — the agent
     * declaring "I'm switching focus to <artifact>". The reducer just
     * stores the latest wire scope; the URL-flip happens in the project
     * page's ``useFocusScopeNavigator`` hook, which watches this slot.
     *
     * Latest-wins. Passing ``null`` clears the slot (used on session
     * change so a draft broadcast on one project doesn't leak into the
     * next session's navigator).
     */
    // TODO: cleanup with research_assistant_gen_3 ff
    remoteFocusScopeReceived(state, action: PayloadAction<{ scope: WireScope | null }>) {
      state.remoteFocusScope = action.payload.scope;
    },
    /**
     * Backend confirmed there is no run in flight for this session.
     *
     * Triggered by the ``kind:'run', status:'idle'`` STATUS_UPDATE the
     * namespace emits on subscribe when the in-memory record was rebuilt
     * empty (typical post-crash / -restart). The persisted message
     * history may still carry tool_call parts marked ``running``; without
     * this signal those would spin forever waiting for terminal events
     * that will never arrive. Close them out as ``error`` and complete
     * any open streaming surfaces — same closing-out semantics as the
     * resume-gap path.
     */
    staleRunCleared(state) {
      for (const message of state.messages) {
        if (message.status === 'streaming') message.status = 'error';
        for (const part of message.parts) {
          if (part.kind === 'tool_call') {
            if (part.toolCall.status === 'running') part.toolCall.status = 'error';
            closeRunningRecursive(part.toolCall.subCalls);
          }
        }
      }
      for (const artifact of state.artifacts) {
        if (artifact.status === 'streaming') artifact.status = 'complete';
      }
    },
    /**
     * Backend reported that the client's resume cursor is older than its
     * ring buffer, so the gap can't be filled. Close out any still-streaming
     * parts as `error` so the continuation renders as a fresh message
     * instead of concatenating onto stale partial prose.
     */
    resumeGapAcknowledged(state) {
      for (const message of state.messages) {
        if (message.status === 'streaming') message.status = 'error';
        for (const part of message.parts) {
          if (part.kind === 'tool_call') {
            if (part.toolCall.status === 'running') part.toolCall.status = 'error';
            closeRunningRecursive(part.toolCall.subCalls);
          }
        }
      }
      for (const artifact of state.artifacts) {
        if (artifact.status === 'streaming') artifact.status = 'complete';
      }
    },
    /**
     * Replace the conversation state with a snapshot loaded from storage.
     *
     * Runtime fields (runState, runId, queue, error) are NOT part of the
     * snapshot — they're deliberately reset here because:
     *   1. The server's in-memory run state was torn down on disconnect, so
     *      there is no in-flight task to rejoin on the client.
     *   2. Any assistant message or tool call marked `streaming` at persist
     *      time was orphaned mid-turn; we close them out with an `error`
     *      status so the UI shows what was there rather than an endless
     *      spinner.
     */
    hydrated(
      state,
      action: PayloadAction<{
        sessionId: string;
        messages: EpiMessage[];
        artifacts: EpiArtifact[];
        activeArtifactId: string | null;
        activeArtifactTab?: EpiArtifactTab | null;
        lastEventSeq?: number;
      }>
    ) {
      // Skip-overwrite guard. Two hydrate paths can fire for a session:
      // localStorage hydrate on focus change, then the backend's
      // ``session_replay`` snapshot when the namespace had no in-memory
      // record. Prefer a recency check over a size check: a
      // ``RESOURCE_UPDATED`` ping (edit/verify/approve on an existing
      // resource) advances the durable ``ui#`` head seq without adding a
      // message or artifact part, so a same-or-fewer-count incoming fold can
      // still be strictly newer — a pure length comparison can't tell the
      // two apart and was letting a stale cached transcript win forever once
      // its part count stopped growing (OR-6344 / OR-6345: an approved AV's
      // chat card silently stayed on its create-time content across every
      // later reconnect). ``lastEventSeq`` is the durable head seq and is
      // monotonic per session, so compare that instead when the incoming
      // payload actually carries one (the full ui#-replay fold and the
      // localStorage rehydrate both do).
      //
      // The one path that does NOT carry a real ``lastEventSeq`` is the
      // lossy Strands-log fallback snapshot (flag-off / no durable ui#
      // stream — see ``session_replay.py``), which is synthesized from
      // separate domain stores and can genuinely under-represent an
      // in-progress turn. Without a trustworthy seq to compare, fall back to
      // the original size heuristic there so a mid-turn refresh (local state
      // has a user message Strands hasn't yet persisted) or a snapshot that
      // raced ahead of live events still can't regress a richer transcript.
      const incomingSeq = action.payload.lastEventSeq;
      const wouldRegress =
        typeof incomingSeq === 'number'
          ? incomingSeq < state.lastEventSeq
          : action.payload.messages.length < state.messages.length ||
            action.payload.artifacts.length < state.artifacts.length;
      state.sessionId = action.payload.sessionId;
      if (!wouldRegress) {
        state.messages = action.payload.messages;
        state.artifacts = action.payload.artifacts;
        state.activeArtifactId = action.payload.activeArtifactId;
        state.activeArtifactTab = action.payload.activeArtifactTab ?? null;
      }
      // Monotonic — never let a rejected/stale fold (or the no-seq fallback
      // snapshot, which defaults to 0) drag the cursor backward. The seq
      // comparison above depends on this holding across repeated hydrates.
      state.lastEventSeq = Math.max(state.lastEventSeq, action.payload.lastEventSeq ?? 0);
      state.runState = 'idle';
      state.runId = null;
      state.queue = [];
      state.error = null;
      state.interruptedRun = null;
      state.initializing = false;
      state.subscribing = false;
      // ``subscribeAcked`` is intentionally NOT touched here. A snapshot says
      // what to render, not whether a request is in flight; only the ``ready``
      // ack carries that. Clearing the submit gate on a localStorage/replay
      // hydrate would reopen the window this gate exists to close (OR-6139).
      // Leave any still-streaming message / tool call in its streaming state.
      // The backend holds a rolling buffer of recent events, so the upcoming
      // `subscribe` with `since_seq` will replay anything we missed into
      // these open parts. If replay doesn't cover the gap (buffer rolled
      // over), the backend signals a "resume gap" and the frontend can
      // decide whether to reset; until that signal arrives we prefer the
      // optimistic path so reconnects feel seamless.
    },
    reset() {
      return initialEpiChatState;
    },
  },
});

export const epiChatActions = slice.actions;
export const epiChatReducer = slice.reducer;
export type EpiChatAction = ReturnType<(typeof slice.actions)[keyof typeof slice.actions]>;
